import { useState } from 'react';
import { DATA_BASE_URL } from '@lib/constants';
import Icon from '@components/common/Icon';

const performances = [
  {
    id: 1,
    title: 'School Recital Performance',
    description: 'My live violin performance at school recital showcasing classical repertoire',
    year: 2020,
    venue: 'ISM Auditorium',
    src: `${DATA_BASE_URL}/data/music/performances/school_recital.mp4`,
    poster: `${DATA_BASE_URL}/data/music/performances/school_recital.mp4`,
    type: 'Recital',
  },
  {
    id: 2,
    title: 'Playing for Granddad',
    description: 'My special performance dedicated to family, featuring personal favorites',
    year: 2024,
    venue: 'Private Performance',
    src: `${DATA_BASE_URL}/data/music/performances/playing_for_granddad.mp4`,
    poster: `${DATA_BASE_URL}/data/music/performances/playing_for_granddad.mp4`,
    type: 'Personal',
  },
];

const PerformancesSection = () => {
  const [selectedPerformance, setSelectedPerformance] = useState(null);

  return (
    <section
      className="py-20 px-4 sm:px-6 lg:px-8"
      style={{
        backgroundColor: 'var(--color-theme-2)',
      }}
    >
      <div className="container mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Icon name="music" size="xl" style={{ color: 'var(--color-accent-6)' }} />
            <h2
              className="text-4xl sm:text-5xl font-heading font-bold tracking-tight"
              style={{ color: 'var(--color-heading)' }}
            >
              Live Performances
            </h2>
          </div>
          <p
            className="text-lg max-w-3xl mx-auto"
            style={{ color: 'var(--color-subtle)' }}
          >
            Watch live violin performances showcasing musical growth and passion
          </p>
        </div>

        {/* Performance Cards - Horizontal Scroll on Mobile */}
        <div className="mb-12">
          <div className="md:grid md:grid-cols-2 md:gap-8 flex md:flex-none overflow-x-auto gap-6 snap-x snap-mandatory scrollbar-hide pb-4 -mx-4 px-4 sm:mx-0 sm:px-0">
            {performances.map((performance) => (
              <div
                key={performance.id}
                onClick={() => setSelectedPerformance(performance)}
                className="group cursor-pointer rounded-2xl overflow-hidden shadow-card transition-all duration-300 hover:shadow-card-hover flex-shrink-0 w-[85vw] sm:w-[70vw] md:w-auto snap-center"
                style={{
                  backgroundColor: 'var(--color-card-bg)',
                  borderColor: 'var(--color-card-border)',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                }}
              >
              {/* Video Thumbnail */}
              <div className="relative aspect-video overflow-hidden bg-black">
                <video
                  src={performance.src}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  preload="metadata"
                />
                <div
                  className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  }}
                >
                  <svg
                    className="w-16 h-16"
                    fill="white"
                    viewBox="0 0 24 24"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>

                {/* Type Badge */}
                <div
                  className="absolute top-3 right-3 px-3 py-1 rounded-full text-sm font-semibold"
                  style={{
                    backgroundColor: 'var(--color-accent-6)',
                    color: 'var(--color-body-bg)',
                  }}
                >
                  {performance.type}
                </div>

                {/* Year Badge */}
                <div
                  className="absolute top-3 left-3 px-3 py-1 rounded-full text-sm font-semibold"
                  style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    color: 'white',
                  }}
                >
                  {performance.year}
                </div>
              </div>

              {/* Performance Info */}
              <div className="p-6 space-y-3">
                <h3
                  className="text-2xl font-semibold"
                  style={{ color: 'var(--color-heading)' }}
                >
                  {performance.title}
                </h3>
                <p
                  className="text-sm"
                  style={{ color: 'var(--color-subtle)' }}
                >
                  {performance.description}
                </p>
                <div
                  className="flex items-center gap-2 pt-2"
                  style={{ color: 'var(--color-muted)' }}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>{performance.venue}</span>
                </div>
              </div>
              </div>
            ))}
          </div>
        </div>

        {/* Featured Video Player Modal */}
        {selectedPerformance && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
            }}
            onClick={() => setSelectedPerformance(null)}
          >
            <div
              className="w-full max-w-5xl rounded-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedPerformance(null)}
                className="absolute top-4 right-4 z-10 p-2 rounded-full transition-colors duration-200"
                style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                }}
                onMouseEnter={(e) => (e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.7)')}
                onMouseLeave={(e) => (e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.5)')}
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {/* Video Player */}
              <video
                controls
                autoPlay
                className="w-full aspect-video"
              >
                <source
                  src={selectedPerformance.src}
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>

              {/* Video Details */}
              <div
                className="p-6 space-y-3"
                style={{
                  backgroundColor: 'var(--color-card-bg)',
                }}
              >
                <h3
                  className="text-2xl font-bold"
                  style={{ color: 'var(--color-heading)' }}
                >
                  {selectedPerformance.title}
                </h3>
                <p
                  className="text-lg"
                  style={{ color: 'var(--color-subtle)' }}
                >
                  {selectedPerformance.description}
                </p>
                <div className="flex flex-wrap gap-6 pt-4">
                  <div className="flex items-center gap-2" style={{ color: 'var(--color-muted)' }}>
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M6 2a1 1 0 000 2h8a1 1 0 100-2H6zM4 5a2 2 0 012-2h8a2 2 0 012 2v10a2 2 0 01-2 2H6a2 2 0 01-2-2V5z" />
                    </svg>
                    <span className="font-semibold">{selectedPerformance.year}</span>
                  </div>
                  <div className="flex items-center gap-2" style={{ color: 'var(--color-muted)' }}>
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="font-semibold">{selectedPerformance.venue}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default PerformancesSection;
